import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Clock, MessageCircle, Target, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const optionLetters = ['A', 'B', 'C', 'D', 'E'];

const PracticeTestAttempt = () => {
  const { testId } = useParams();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState(null);
  const [startTime] = useState(Date.now());
  const mistakeOptions = [
    'Conceptual',
    'Calculation',
    'Misreading',
    'Casual',
    'Time Management'
  ];

  useEffect(() => {
    fetchTest();
    fetchSettings();
  }, [testId]);

  const fetchTest = async () => {
    try {
      const response = await axios.get(`${API}/practice-tests/${testId}`);
      setTest(response.data);
    } catch (error) {
      toast.error('Failed to load test');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.warn('Settings load failed (non-critical):', error?.message);
    }
  };

  const handleAnswerChange = (questionId, value, isMulti = false) => {
    setAnswers((prev) => {
      if (!isMulti) {
        return { ...prev, [questionId]: value };
      }
      const current = prev[questionId] || [];
      const exists = current.includes(value);
      const updated = exists ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [questionId]: updated };
    });
  };

  const handleSubmit = async () => {
    if (!test) return;
    setSubmitting(true);
    try {
      const elapsed = Math.round((Date.now() - startTime) / 1000);
      const perQuestion = test.questions.length ? elapsed / test.questions.length : 0;
      const timeSpent = test.questions.reduce((acc, question) => {
        acc[question.id] = perQuestion;
        return acc;
      }, {});

      const response = await axios.post(`${API}/practice-tests/${testId}/submit`, {
        answers,
        time_spent: timeSpent
      });
      const summaries = (response.data.question_summaries || []).map((item, index) => ({
        ...item,
        local_id: item.question_id || `q-${index}`
      }));
      setResult({ ...response.data, question_summaries: summaries });
      toast.success('Test submitted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMistakeChange = (questionId, value) => {
    setResult((prev) => {
      if (!prev?.question_summaries) return prev;
      const updated = prev.question_summaries.map((item) =>
        item.local_id === questionId ? { ...item, mistake_type: value } : item
      );
      return { ...prev, question_summaries: updated };
    });
  };

  const handleSaveMistakes = async () => {
    if (!result?.attempt_id) return;
    const mistakes = (result.question_summaries || [])
      .filter((item) => !item.is_correct && item.mistake_type)
      .map((item) => ({ question_id: item.question_id || item.local_id, mistake_type: item.mistake_type }));

    try {
      const response = await axios.put(`${API}/practice-history/${result.attempt_id}/mistakes`, { mistakes });
      setResult((prev) => {
        const mergedSummaries = (response.data.question_summaries || prev.question_summaries || []).map((item, index) => ({
          ...item,
          local_id: item.question_id || prev.question_summaries?.[index]?.local_id || `q-${index}`
        }));
        return { ...prev, ...response.data, question_summaries: mergedSummaries };
      });
      toast.success('Mistake patterns updated');
    } catch (error) {
      toast.error('Failed to update mistake patterns');
    }
  };

  const counselorWhatsapp = useMemo(() => settings?.counselor_whatsapp?.replace(/[^0-9]/g, ''), [settings]);

  if (!test) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="practice-test-loading">
        <div className="text-lg text-slate-600">Loading test...</div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="practice-test-page">
      <div className="mb-6">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">← Back to Dashboard</Link>
        <h1 className="text-4xl font-bold text-slate-900 mt-2">{test.exam} Practice Test</h1>
        <p className="text-slate-600">{test.mode === 'full' ? 'Full Test' : 'Part Test'} · {test.level}</p>
      </div>

      {!result ? (
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-violet-600" />
              Answer the Questions
            </CardTitle>
            <CardDescription>Submit when you are done to get instant analysis.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {test.questions.map((question, index) => (
              <div key={question.id} className="p-4 border border-slate-200 rounded-xl">
                {question.passage && (
                  <div className="mb-3 p-3 rounded-lg bg-slate-50 text-sm text-slate-700" data-testid={`practice-passage-${index}`}>
                    <strong>Passage:</strong> {question.passage}
                  </div>
                )}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-slate-900">
                      Q{index + 1}. {question.topic && question.question_text?.startsWith(question.topic)
                        ? question.question_text.replace(`${question.topic}: `, '')
                        : question.question_text}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      <Badge className="bg-violet-100 text-violet-700 border-0">{question.subject}</Badge>
                      <Badge className="bg-slate-100 text-slate-600 border-0">{question.chapter}</Badge>
                      <Badge className="bg-emerald-100 text-emerald-700 border-0">{question.question_type}</Badge>
                    </div>
                  </div>
                  <div className="text-sm text-slate-500">+{question.marks} / -{question.negative_marks}</div>
                </div>

                {question.image_url && (
                  <div className="mt-4">
                    <img
                      src={question.image_url}
                      alt="Question diagram"
                      className="w-full max-w-md rounded-xl border border-slate-200"
                      data-testid={`practice-question-image-${index}`}
                    />
                  </div>
                )}

                {question.question_type === 'mcq_single' && (
                  <div className="mt-4 space-y-2">
                    {question.options?.map((option, optIndex) => {
                      const letter = optionLetters[optIndex] || optIndex + 1;
                      return (
                        <label key={option} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="radio"
                            name={`question-${question.id}`}
                            value={letter}
                            checked={answers[question.id] === letter}
                            onChange={() => handleAnswerChange(question.id, letter)}
                            data-testid={`practice-answer-${question.id}-${letter}`}
                          />
                          <span><strong>{letter}.</strong> {option}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {question.question_type === 'mcq_multi' && (
                  <div className="mt-4 space-y-2">
                    {question.options?.map((option, optIndex) => {
                      const letter = optionLetters[optIndex] || optIndex + 1;
                      return (
                        <label key={option} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            value={letter}
                            checked={(answers[question.id] || []).includes(letter)}
                            onChange={() => handleAnswerChange(question.id, letter, true)}
                            data-testid={`practice-answer-multi-${question.id}-${letter}`}
                          />
                          <span><strong>{letter}.</strong> {option}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {question.question_type === 'numerical' && (
                  <div className="mt-4">
                    <Input
                      type="text"
                      placeholder="Enter numerical answer"
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      data-testid={`practice-answer-numerical-${question.id}`}
                    />
                  </div>
                )}

                {question.question_type === 'paragraph' && (
                  <div className="mt-4">
                    <Input
                      type="text"
                      placeholder="Enter answer"
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      data-testid={`practice-answer-paragraph-${question.id}`}
                    />
                  </div>
                )}
              </div>
            ))}

            <Button
              onClick={handleSubmit}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-12"
              disabled={submitting}
              data-testid="practice-submit-button"
            >
              {submitting ? 'Submitting...' : 'Submit Test & View Analysis'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6" data-testid="practice-test-results">
          {result.attempt_id && (
            <div className="flex justify-end">
              <Link
                to={`/practice/results/${result.attempt_id}`}
                className="text-sm font-semibold text-violet-600"
                data-testid="practice-view-saved-result"
              >
                View Saved Result →
              </Link>
            </div>
          )}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                Your Score
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-4xl font-bold text-slate-900" data-testid="practice-score">
                  {result.score} / {result.max_score}
                </p>
                <p className="text-slate-600" data-testid="practice-percentage">{result.percentage}% overall</p>
                <div className="text-sm text-slate-500 mt-1 space-y-0.5" data-testid="practice-score-breakdown">
                  <p>
                    <span className="text-emerald-600 font-semibold">Positive +{result.positive_score ?? result.score}</span>
                    {' · '}
                    <span className="text-rose-600 font-semibold">Negative −{result.penalty ?? 0}</span>
                  </p>
                  <p>
                    {result.correct_count ?? 0} of {result.total_questions ?? '—'} correct
                    {result.accuracy !== undefined && <> · {result.accuracy}% accuracy</>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Instant analysis generated</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-violet-600" />
                Performance Analysis
              </CardTitle>
              <CardDescription>{result.analysis?.overall_insights}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="subjects">
                <TabsList className="mb-4">
                  <TabsTrigger value="subjects">Subjects</TabsTrigger>
                  <TabsTrigger value="chapters">Chapters</TabsTrigger>
                  <TabsTrigger value="topics">Topics</TabsTrigger>
                  <TabsTrigger value="mistakes">Mistakes</TabsTrigger>
                  <TabsTrigger value="time">Time</TabsTrigger>
                </TabsList>
                <TabsContent value="subjects">
                  <div className="space-y-3">
                    {Object.entries(result.analysis?.subject_analysis || {}).map(([key, value]) => (
                      <div key={key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">{key}</h4>
                        <p className="text-slate-700">{value}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="chapters">
                  <div className="space-y-3">
                    {Object.entries(result.analysis?.chapter_analysis || {}).map(([key, value]) => (
                      <div key={key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">{key}</h4>
                        <p className="text-slate-700">{value}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="topics">
                  <div className="space-y-3">
                    {Object.entries(result.analysis?.topic_analysis || {}).map(([key, value]) => (
                      <div key={key} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">{key}</h4>
                        <p className="text-slate-700">{value}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="mistakes">
                  <div className="space-y-3">
                    {Object.entries(result.analysis?.mistake_patterns || {}).map(([key, value]) => (
                      <div key={key} className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <h4 className="font-semibold text-amber-800 mb-2">{key}</h4>
                        <p className="text-amber-800">{value}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="time">
                  <div className="space-y-3">
                    {Object.entries(result.analysis?.time_efficiency || {}).map(([key, value]) => (
                      <div key={key} className="p-4 rounded-xl bg-violet-50 border border-violet-200">
                        <h4 className="font-semibold text-violet-800 mb-2">{key}</h4>
                        <p className="text-violet-800">{value}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg" data-testid="practice-question-review">
            <CardHeader>
              <CardTitle>Question-wise Review</CardTitle>
              <CardDescription>Tag the mistake type for incorrect answers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(result.question_summaries || []).map((item, index) => {
                const questionKey = item.local_id || item.question_id || `q-${index}`;
                return (
                <div key={questionKey} className="p-4 rounded-xl border border-slate-200">
                  <p className="font-semibold text-slate-900 mb-1">
                    Q{index + 1}. {item.topic && item.question_text?.startsWith(item.topic)
                      ? item.question_text.replace(`${item.topic}: `, '')
                      : item.question_text}
                  </p>
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt="Question diagram"
                      className="w-full max-w-md rounded-xl border border-slate-200 my-3"
                      data-testid={`practice-review-image-${index}`}
                    />
                  )}
                  <p className="text-sm text-slate-600">Your answer: {item.student_answer || '—'} · Correct: {item.correct_answer || '—'}</p>
                  {item.explanation && (
                    <p className="text-sm text-slate-600 mt-2">Explanation: {item.explanation}</p>
                  )}
                  {item.solution_image_url && (
                    <img
                      src={item.solution_image_url}
                      alt="Solution diagram"
                      className="w-full max-w-md rounded-xl border border-violet-200 bg-white mt-2"
                      data-testid={`practice-review-solution-image-${index}`}
                    />
                  )}
                  {!item.is_correct && (
                    <div className="mt-3">
                      <Select
                        value={item.mistake_type || ''}
                        onValueChange={(value) => handleMistakeChange(questionKey, value)}
                      >
                        <SelectTrigger data-testid={`practice-mistake-${questionKey}`}>
                          <SelectValue placeholder="Select mistake type" />
                        </SelectTrigger>
                        <SelectContent>
                          {mistakeOptions.map((option) => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              );
              })}

              <Button
                onClick={handleSaveMistakes}
                variant="outline"
                className="rounded-full"
                data-testid="practice-save-mistakes"
              >
                Update Mistake Analysis
              </Button>
            </CardContent>
          </Card>

          {result.analysis?.recommendations?.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Implementable Suggestions
                </CardTitle>
                <CardDescription>{result.analysis?.way_forward}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {result.analysis.recommendations.map((rec, index) => (
                  <motion.div
                    key={rec}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200"
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p className="text-slate-700" data-testid={`practice-suggestion-${index}`}>{rec}</p>
                  </motion.div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Book Session Recommendations</CardTitle>
              <CardDescription>Book a teacher for your weak topics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {result.teacher_recommendations?.length > 0 ? (
                result.teacher_recommendations.map((teacher) => (
                  <div key={teacher.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
                    <div>
                      <p className="font-semibold text-slate-900">{teacher.name}</p>
                      <p className="text-sm text-slate-600">{teacher.subjects?.join(', ')}</p>
                    </div>
                    <Link to={`/teacher/${teacher.id}`} className="text-sm text-violet-600 font-medium">
                      Book Session
                    </Link>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No teacher recommendations available yet.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-emerald-600" />
                Talk to a Counselor
              </CardTitle>
              <CardDescription>Get guidance on the best next steps.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-sm text-slate-700">
                  <p className="font-semibold">{settings?.counselor_name || 'Education Counselor'}</p>
                  <p>{settings?.counselor_whatsapp || 'WhatsApp not set'}</p>
                </div>
                {counselorWhatsapp ? (
                  <Button
                    asChild
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                    data-testid="practice-counselor-whatsapp"
                  >
                    <a
                      href={`https://wa.me/${counselorWhatsapp}?text=${encodeURIComponent('Hi, I need guidance after my practice test.')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Chat on WhatsApp
                    </a>
                  </Button>
                ) : (
                  <Button variant="outline" disabled>WhatsApp not available</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PracticeTestAttempt;